import React from "react";
import SearchResults from "../components/SearchResults";
import FilterSection from "../components/Filter";
import "../styles/search.css";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import {
  fetchFilteredProducts,
  fetchAdvancedSearch,
} from "../actions/FilterActions";
import { fetchCategories } from "../actions/categoriesActions";
import { useLocation } from "react-router-dom";
import { advancedSearch } from "../utils/searchUtils";

function Search() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { loading, filteredProducts, count } = useSelector(
    (state) => state.filters,
  );
  const {
    results: advancedSearchResults = [],
    suggestions: searchSuggestions = [],
    totalResults: advancedSearchCount = 0,
    loading: advancedSearchLoading = false,
  } = useSelector((state) => state.advancedSearch || {});
  const { categories } = useSelector((state) => state.categories);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(24);
  const [totalPages, setTotalPages] = useState(
    Math.ceil(count / productsPerPage),
  );
  // const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({
    category_id: [],
    min_price: 0,
    max_price: 10000000,
    skin_concern: [],
    skin_type: [],
    fomulation: [],
    keywords: [],
    brand: "",
    features: [],
    tags: [],
    search: "",
    status: "published",
  });

  const [query, setQuery] = useState(null);
  const [enhancedResults, setEnhancedResults] = useState([]);

  // Handle URL parameters and initial search
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const q = searchParams.get("q");
    const b = searchParams.get("b");
    const categoryParam = searchParams.get("category");
    setQuery(q);

    // Fetch categories first if not loaded
    if (!categories || categories.length === 0) {
      dispatch(fetchCategories());
      return;
    }

    // Helper function to collect all nested category IDs
    const collectAllCategoryIds = (category) => {
      const ids = [category.category_id];
      if (
        category.children &&
        Array.isArray(category.children) &&
        category.children.length > 0
      ) {
        category.children.forEach((child) => {
          ids.push(...collectAllCategoryIds(child));
        });
      }
      return ids;
    };

    // Helper function to find category by ID recursively
    const findCategoryById = (catList, targetId) => {
      if (!catList || !Array.isArray(catList)) return null;
      for (const cat of catList) {
        if (
          cat.category_id === targetId ||
          cat.category_id?.toString() === targetId?.toString()
        ) {
          return cat;
        }
        if (
          cat.children &&
          Array.isArray(cat.children) &&
          cat.children.length > 0
        ) {
          const found = findCategoryById(cat.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    // Process category filter if category parameter exists
    let categoryIds = [];
    if (categoryParam && categories && categories.length > 0) {
      const category = findCategoryById(categories, categoryParam);
      if (category) {
        categoryIds = collectAllCategoryIds(category);
      }
    }

    const newFilters = {
      category_id: categoryIds,
      min_price: 0,
      max_price: 10000000,
      skin_concern: [],
      skin_type: [],
      fomulation: [],
      keywords: [],
      brand: b || "",
      features: [],
      tags: [],
      search: q || "",
      status: "published",
    };

    setFilters(newFilters);
  }, [dispatch, location, categories]);

  // Handle filter changes and pagination
  useEffect(() => {
    dispatch(
      fetchFilteredProducts({
        ...filters,
        page: currentPage,
        pagination: productsPerPage,
      }),
    );
    setTotalPages(Math.ceil(count / productsPerPage));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [filters, currentPage, productsPerPage, dispatch, count]);

  // Enhanced search using backend advanced search
  useEffect(() => {
    if (query && query.trim()) {
      // Use backend advanced search for better performance and accuracy
      dispatch(
        fetchAdvancedSearch({
          search: query,
          max_results: 1000,
          fields: ["name", "brand", "description", "keywords", "tags"],
        }),
      );
    } else {
      // For brand-only searches (no query), use filtered products directly
      setEnhancedResults(filteredProducts || []);
    }
  }, [query, dispatch, filteredProducts]);

  // Update enhanced results when advanced search completes
  useEffect(() => {
    if (advancedSearchResults.length > 0 && query) {
      // Only use advanced search results if we have a query
      setEnhancedResults(advancedSearchResults);
    } else if (query && filteredProducts) {
      // Fallback to client-side search if backend search fails
      const enhanced = advancedSearch(filteredProducts, query, {
        threshold: 0.5,
        searchFields: ["name", "brand", "description", "keywords", "tags"],
        maxResults: 1000,
      });
      setEnhancedResults(enhanced);
    }
    // For brand-only searches (no query), enhancedResults is already set in the previous useEffect
  }, [advancedSearchResults, query, filteredProducts]);
  return (
    <div className="search-container">
      <div className="search-content relative">
        <FilterSection
          isSearchPage={true}
          filters={filters}
          setFilters={setFilters}
          categories={categories}
        />
        <SearchResults
          filters={filters}
          currentPage={currentPage}
          totalPages={totalPages}
          productsPerPage={productsPerPage}
          setCurrentPage={setCurrentPage}
          filteredProducts={enhancedResults}
          loading={loading}
          query={query}
        />
      </div>
    </div>
  );
}

export default Search;
